import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";

import {
  ConnectionDim,
  ConnectTeleopdButton,
  isConnected,
  NotConnected,
  TenantIdentity,
} from "Components/Tenants";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  Page,
  PageHeader,
  StatCard,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
} from "Components/ui";
import {
  CalendarClock,
  IndianRupee,
  Lock,
  ReceiptText,
  RefreshCw,
  Search,
  TriangleAlert,
  Unlock,
  Wallet,
} from "Components/ui/icons";
import { getApiErrorMessage } from "api/apiError";
import { BillingOverviewRow } from "api/billing";
import { useAuth } from "hooks/useAuth";
import { cn } from "utils/cn";
import { formatDate, formatDaysAgo, formatINR, pluralize, todayIST } from "utils/format";
import useBillingOverviewData, { OVERVIEW_FILTERS } from "./data";
import CollectionsSheet from "./Components/CollectionsSheet";
import OutstandingSheet from "./Components/OutstandingSheet";
import { BillingStatusChip, ENFORCEMENT_LABELS } from "./Components/StatusChips";
import TeleopdLinkBadge from "./Components/TeleopdLinkBadge";
import { useNow } from "./hooks";
import { OverviewFilter } from "./types";

const buildColumns = (today: string, canConnect: boolean): ColumnDef<BillingOverviewRow>[] => [
  {
    id: "client",
    header: "Client",
    cell: ({ row }) => {
      const connected = isConnected(row.original.link);
      return (
        <div className="min-w-0 py-2">
          <TenantIdentity
            name={row.original.tenantName}
            slug={row.original.slug}
            uri={row.original.uri}
            muted={!connected}
          />
          {connected && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {ENFORCEMENT_LABELS[row.original.enforcement]}
            </p>
          )}
        </div>
      );
    },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <ConnectionDim link={row.original.link}>
        <BillingStatusChip status={row.original.status} />
      </ConnectionDim>
    ),
  },
  {
    id: "nextDue",
    header: "Next due",
    meta: { className: "whitespace-nowrap" },
    cell: ({ row }) => {
      const { nextDue } = row.original;
      if (!nextDue) {
        return <span className="text-subtle-foreground">—</span>;
      }
      return (
        <ConnectionDim link={row.original.link}>
          <p
            className={cn(
              "font-medium",
              nextDue.date < today ? "text-danger-text" : "text-foreground"
            )}
          >
            {formatDate(nextDue.date)}
          </p>
          <p className="text-xs tabular-nums text-muted-foreground">{formatINR(nextDue.amount)}</p>
        </ConnectionDim>
      );
    },
  },
  {
    id: "outstanding",
    header: "Outstanding",
    meta: { className: "whitespace-nowrap" },
    cell: ({ row }) => (
      <ConnectionDim link={row.original.link}>
        <p
          className={cn(
            "font-semibold tabular-nums",
            row.original.outstanding > 0 ? "text-foreground" : "text-subtle-foreground"
          )}
        >
          {formatINR(row.original.outstanding)}
        </p>
        <p className="text-xs text-muted-foreground">
          {row.original.lastPaymentOn
            ? `Paid ${formatDaysAgo(row.original.lastPaymentOn)}`
            : "No payments yet"}
        </p>
      </ConnectionDim>
    ),
  },
  {
    id: "plan",
    header: "Plan",
    cell: ({ row }) => (
      <ConnectionDim link={row.original.link}>
        <p className="max-w-xs text-muted-foreground">
          {row.original.planSummary || (
            <span className="text-subtle-foreground">No recurring charge</span>
          )}
        </p>
      </ConnectionDim>
    ),
  },
  {
    id: "teleopd",
    header: "TeleOpd",
    cell: ({ row }) => {
      const { link, stage, tenantId } = row.original;
      // Not connected: no click-through; the only step offered is connecting its TeleOpd
      // (Locked clients have to be made actionable on the Tenants page first).
      if (isConnected(link)) {
        return <TeleopdLinkBadge linked lastSyncAt={link.lastSyncAt} />;
      }
      return stage === "beta" && canConnect ? (
        <ConnectTeleopdButton tenantId={tenantId} />
      ) : (
        <NotConnected className="text-xs" />
      );
    },
  },
];

interface StageSectionProps {
  title: string;
  description: string;
  badge: React.ReactNode;
  icon: React.ReactNode;
  rows: BillingOverviewRow[];
  columns: ColumnDef<BillingOverviewRow>[];
  onOpen: (row: BillingOverviewRow) => void;
}

const StageSection: React.FC<StageSectionProps> = ({
  title,
  description,
  badge,
  icon,
  rows,
  columns,
  onOpen,
}) => (
  <Card>
    <CardHeader
      title={
        <span className="flex flex-wrap items-center gap-2">
          {title}
          <span className="text-xs font-normal tabular-nums text-muted-foreground">
            {pluralize(rows.length, "client")}
          </span>
        </span>
      }
      description={description}
      actions={badge}
    />
    <div className="px-5 pb-5 pt-4">
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => row.tenantId}
        onRowClick={onOpen}
        isRowClickable={(row) => isConnected(row.link)}
        enableSorting={false}
        caption={`${title} clients`}
        empty={<EmptyState compact icon={icon} title="No clients here for this filter" />}
      />
    </div>
  </Card>
);

// The report drawers live in the URL (?report=…), so Back, Escape and shared links all work.
type ReportName = "collections" | "outstanding";
const REPORT_PARAM = "report";
const OVERDUE_PARAM = "overdue";

const reportSearch = (report: ReportName, overdueOnly = false) =>
  `?${REPORT_PARAM}=${report}${overdueOnly ? `&${OVERDUE_PARAM}=1` : ""}`;

const readReport = (search: string): { report: ReportName | null; overdueOnly: boolean } => {
  const params = new URLSearchParams(search);
  const value = params.get(REPORT_PARAM);
  return {
    report: value === "collections" || value === "outstanding" ? value : null,
    overdueOnly: params.get(OVERDUE_PARAM) === "1",
  };
};

const BillingOverviewPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { report, overdueOnly } = readReport(location.search);
  const openReport = (name: ReportName, overdue = false) =>
    history.push({ pathname: location.pathname, search: reportSearch(name, overdue) });
  const onReportOpenChange = (open: boolean) => {
    if (!open) {
      history.replace({ pathname: location.pathname });
    }
  };
  const { hasPermission } = useAuth();
  const canConnect = hasPermission("billing", "configure");
  useNow();
  const { states, setFilter, setSearch, refetch } = useBillingOverviewData();
  const {
    kpis,
    loading,
    refreshing,
    error,
    rows,
    visibleRows,
    actionableRows,
    lockedRows,
    counts,
    filter,
    search,
  } = states;
  const today = todayIST();
  const columns = React.useMemo(() => buildColumns(today, canConnect), [today, canConnect]);
  const openRow = (row: BillingOverviewRow) => history.push(`/billing/${row.tenantId}`);
  const clearFilters = () => {
    setFilter("all");
    setSearch("");
  };

  let list: React.ReactNode;
  if (error) {
    list = (
      <Card>
        <ErrorState
          title="Billing overview didn't load"
          description={getApiErrorMessage(error)}
          action={
            <Button variant="secondary" leftIcon={<RefreshCw />} onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      </Card>
    );
  } else if (loading) {
    list = (
      <DataTable columns={columns} data={[]} loading enableSorting={false} caption="Clients" />
    );
  } else if (rows.length === 0) {
    list = (
      <Card>
        <EmptyState
          icon={<ReceiptText />}
          title="No clients yet"
          description="Clients appear here once they are added under Tenants."
        />
      </Card>
    );
  } else if (visibleRows.length === 0) {
    list = (
      <Card>
        <EmptyState
          icon={<Search />}
          title="No clients match"
          description="Try another filter or search term."
          action={
            <Button size="sm" variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      </Card>
    );
  } else {
    list = (
      <div className="space-y-6">
        <StageSection
          title="Actionable"
          description="Beta: once a client's TeleOpd is connected, its charges, invoices, payments and rules can be changed."
          badge={<StatusBadge domain="stage" status="beta" />}
          icon={<Unlock />}
          rows={actionableRows}
          columns={columns}
          onOpen={openRow}
        />
        <StageSection
          title="Locked"
          description="Read-only until the production release. Connected clients open for viewing; the others are greyed out."
          badge={<Badge tone="neutral">Until production release</Badge>}
          icon={<Lock />}
          rows={lockedRows}
          columns={columns}
          onOpen={openRow}
        />
        <p className="text-xs text-muted-foreground">
          Showing {visibleRows.length} of {pluralize(rows.length, "client")}, most urgent first.
        </p>
      </div>
    );
  }

  return (
    <Page width="wide">
      <PageHeader
        title="Billing"
        description="What every client owes, when it is due, and what TeleOpd shows their staff."
        actions={
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            loading={refreshing}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Outstanding"
          value={kpis ? formatINR(kpis.outstanding) : "—"}
          hint="Unpaid balance on issued invoices"
          icon={<Wallet />}
          tone="primary"
          loading={loading}
          aria-haspopup="dialog"
          onClick={() => openReport("outstanding")}
        />
        <StatCard
          label="Overdue"
          value={kpis ? formatINR(kpis.overdueAmount) : "—"}
          hint={kpis ? `${pluralize(kpis.overdueCount, "client")} past due` : undefined}
          icon={<TriangleAlert />}
          tone={kpis && kpis.overdueAmount > 0 ? "danger" : "neutral"}
          loading={loading}
          aria-haspopup="dialog"
          onClick={() => openReport("outstanding", true)}
        />
        <StatCard
          label="Due soon"
          value={kpis ? pluralize(kpis.dueSoonCount, "client") : "—"}
          hint="Inside their warning window (7 days by default)"
          icon={<CalendarClock />}
          tone="warning"
          loading={loading}
        />
        <StatCard
          label="Read-only now"
          value={kpis ? pluralize(kpis.readOnlyCount, "client") : "—"}
          hint="TeleOpd blocks staff writes"
          icon={<Lock />}
          tone={kpis && kpis.readOnlyCount > 0 ? "danger" : "neutral"}
          loading={loading}
        />
        <StatCard
          label="Collected this month"
          value={kpis ? formatINR(kpis.collectedThisMonth) : "—"}
          hint="Payments received (IST month)"
          icon={<IndianRupee />}
          tone="success"
          loading={loading}
          aria-haspopup="dialog"
          onClick={() => openReport("collections")}
        />
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={filter} onValueChange={(value) => setFilter(value as OverviewFilter)}>
          <TabsList
            variant="pill"
            aria-label="Filter clients"
            className="max-w-full overflow-x-auto"
          >
            {OVERVIEW_FILTERS.map((option) => (
              <TabsTrigger key={option.id} value={option.id} count={counts[option.id]}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search clients…"
          aria-label="Search clients"
          leading={<Search />}
          wrapperClassName="w-full lg:max-w-xs"
        />
      </div>

      {list}

      <CollectionsSheet open={report === "collections"} onOpenChange={onReportOpenChange} />
      <OutstandingSheet
        open={report === "outstanding"}
        onOpenChange={onReportOpenChange}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={(on) =>
          history.replace({ pathname: location.pathname, search: reportSearch("outstanding", on) })
        }
      />
    </Page>
  );
};

export default BillingOverviewPage;
